import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "./cn";
import { UiLink, type UiLinkProps } from "./router";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "quiet";
type Size = "sm" | "md" | "lg";

const base =
  "inline-flex items-center justify-center gap-2 rounded-field font-semibold whitespace-nowrap " +
  "transition-[background,box-shadow,color,transform] duration-150 ease-out-soft " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand " +
  "active:translate-y-px disabled:pointer-events-none disabled:opacity-50";

const variants: Record<Variant, string> = {
  primary:
    "bg-brand-button text-on-brand shadow-brand hover:brightness-[1.06] hover:shadow-[0_8px_20px_-4px_rgb(47_77_235/0.5)]",
  secondary:
    "bg-surface text-ink-2 border border-line hover:border-ink-4/50 hover:bg-surface-sunken/60 shadow-card",
  ghost: "text-ink-2 hover:bg-surface-sunken hover:text-ink",
  danger: "bg-danger text-white hover:brightness-105 shadow-card",
  quiet: "text-brand hover:bg-brand-soft/70",
};

const sizes: Record<Size, string> = {
  sm: "h-8 px-3 text-sm",
  md: "h-10 px-4 text-base",
  lg: "h-12 px-6 text-md",
};

type CommonProps = {
  variant?: Variant;
  size?: Size;
  className?: string;
  children: ReactNode;
};

type ButtonProps = CommonProps &
  ButtonHTMLAttributes<HTMLButtonElement> & { href?: undefined };
type AnchorProps = CommonProps & Omit<UiLinkProps, "className"> & { href: string };

export function Button(props: ButtonProps | AnchorProps) {
  const { variant = "primary", size = "md", className, children, ...rest } = props;
  const classes = cn(base, variants[variant], sizes[size], className);

  // Polymorphic exactly as the design has it: an `href` makes this a link, and
  // UiLink decides between a client-side route and a plain anchor from there.
  if ("href" in rest && rest.href) {
    return (
      <UiLink {...(rest as AnchorProps)} className={classes}>
        {children}
      </UiLink>
    );
  }
  return (
    <button {...(rest as ButtonProps)} className={classes}>
      {children}
    </button>
  );
}
